import { useState } from 'react';
import { useForm } from "react-hook-form";
import Checkbox from "@/components/forms/checkbox";
import SmallSpinner from "@/components/shared/spinners/small-spinner";
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { BellRing, Save, CheckCircle2 } from 'lucide-react';
import { useCurrentUser } from '@/hooks/api/use-user';
import { useUpdateCommunicationPreferences } from '@/hooks/api/use-profile';

interface ChangeAbonnementsValues {
    optin: boolean;
    optin_partenaires: boolean;
    optin_pixel: boolean;
}

const ChangeAbonnements = () => {
    const user = useCurrentUser();
    const postCommunicationsPreferences = useUpdateCommunicationPreferences();
    const loading = postCommunicationsPreferences.isPending;
    const [success, setSuccess] = useState(false);

    const { handleSubmit, watch, setValue } = useForm<ChangeAbonnementsValues>({
        defaultValues: {
            optin: Boolean(user.optin) || false,
            optin_partenaires: Boolean(user.optin_partenaires) || false,
            // Le pixel est opt-in par défaut : ?? et non || pour ne pas écraser true
            optin_pixel: (user.optin_pixel ?? true) as boolean,
        },
    });

    const onSubmit = (values: ChangeAbonnementsValues) => {
        postCommunicationsPreferences.mutate({
            form: {
                optin: values.optin ? 'true' : 'false',
                optin_partenaires: values.optin_partenaires ? 'true' : 'false',
                optin_pixel: values.optin_pixel ? 'true' : 'false',
            },
            successFunction: () => {
                setSuccess(true);
            },
        });
    };

    return (
        <div className="space-y-6">
            <div className="space-y-2 sm:space-y-3 mt-4">
                <h1 className="text-xl sm:text-2xl font-bold">Je modifie mes préférences de communication</h1>
            </div>

            {success && (
                <Alert variant="success">
                    <CheckCircle2 />
                    <AlertTitle className="text-black">Nous avons bien pris en compte votre demande de changement.</AlertTitle>
                    <AlertDescription>La modification sera effective sous 72h.</AlertDescription>
                </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <BellRing className="w-5 h-5 text-primary" />
                            Préférences de communication
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-5">
                        <Checkbox
                            name="optin"
                            label={
                                <span className="flex flex-col gap-0.5">
                                    <span>Je souhaite recevoir les emails relationnels</span>
                                    <span className="text-xs text-muted-foreground">
                                        (notamment des informations et conseils liés à mes garanties, services et
                                        avantages client)
                                    </span>
                                </span>
                            }
                            checked={watch('optin')}
                            onChange={(e) => setValue('optin', e.target.value)}
                        />
                        <Checkbox
                            name="optin_partenaires"
                            label={
                                <span className="flex flex-col gap-0.5">
                                    <span>Je souhaite recevoir les emails promotionnels</span>
                                    <span className="text-xs text-muted-foreground">
                                        (bons plans, offres spéciales, réductions et actualités commerciales)
                                    </span>
                                </span>
                            }
                            checked={watch('optin_partenaires')}
                            onChange={(e) => setValue('optin_partenaires', e.target.value)}
                        />
                        <Checkbox
                            name="optin_pixel"
                            label={
                                <span>
                                    J'accepte l'utilisation d'outils de mesure d'audience dans les e-mails (suivi des
                                    ouvertures et suivi des clics sur les liens) afin d'améliorer la pertinence des
                                    communications reçues.
                                </span>
                            }
                            checked={watch('optin_pixel')}
                            onChange={(e) => setValue('optin_pixel', e.target.value)}
                        />

                        <div className="pt-2">
                            {loading ? (
                                <SmallSpinner />
                            ) : (
                                <Button type="submit" className="w-full text-white cursor-pointer whitespace-normal py-6">
                                    <Save className="w-4 h-4 shrink-0" />
                                    Je valide mes nouvelles préférences de communication
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </form>
        </div>
    );
};

export default ChangeAbonnements;
